import { useEffect, useState } from 'react';
import { Button } from '../primitives/Button.js';
import { Modal } from '../primitives/Modal.js';
import { Text } from '../primitives/Text.js';

export interface ConfirmDeleteOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void | Promise<void>;
}

type Setter = (opts: ConfirmDeleteOptions | null) => void;
let _setConfirm: Setter | null = null;

/** Mount once near app root — wires `openConfirmDelete` to a modal. */
export function ConfirmDeleteHost() {
  const [state, setState] = useState<ConfirmDeleteOptions | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    _setConfirm = (next) => {
      setPending(false);
      setError(null);
      setState(next);
    };
    return () => {
      _setConfirm = null;
    };
  }, []);
  const close = () => {
    if (pending) return;
    setState(null);
  };
  const confirm = async () => {
    if (!state || pending) return;
    setPending(true);
    setError(null);
    try {
      await state.onConfirm();
      setState(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'La suppression a échoué.';
      setError(message);
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      opened={!!state}
      onClose={close}
      title={state?.title ?? ''}
      footer={
        state ? (
          <>
            <Button variant="ghost" onClick={close} disabled={pending}>
              Annuler
            </Button>
            <Button variant="danger" onClick={confirm} disabled={pending}>
              {pending ? 'Suppression…' : state.confirmLabel ?? 'Supprimer'}
            </Button>
          </>
        ) : null
      }
    >
      {state ? (
        <>
          <Text size="sm">{state.message}</Text>
          {error ? (
            <Text size="sm" c="error" style={{ marginTop: 'var(--wp-space-8)' }}>
              {error}
            </Text>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

export function openConfirmDelete({ title, message, confirmLabel = 'Supprimer', onConfirm }: ConfirmDeleteOptions) {
  _setConfirm?.({ title, message, confirmLabel, onConfirm });
}
